// End of day: the hub for managers and owners (check-ins, numbers, close), the check-in for staff.
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAppColors } from '@/src/lib/theme';
import { tradingDate } from '@/src/lib/checkin';
import type { EodStepId } from '@/src/lib/eodSteps';
import { useEodNight } from '@/src/hooks/useEodNight';
import EodPage from './eod-page';
import EodHub from './eod-hub';
import EodManagerPage from './eod-manager-page';

const EodNumbersPage = lazy(() => import('./eod-numbers-page'));

export type EodView = 'hub' | 'team' | 'numbers' | 'checkin';

interface Props {
  venueId: string;
  venueName: string;
  tz: string | null;
  userId: string;
  userName: string;
  userRole: string;
  /** Asked for from elsewhere (a task link, the Numbers page); `n` changes each time. */
  request?: { view: EodView; n: number } | null;
}

export default function EodSection({ venueId, venueName, tz, userId, userName, userRole, request }: Props) {
  const isManager = userRole === 'owner' || userRole === 'manager';
  if (!isManager) return <EodPage venueId={venueId} userId={userId} userRole={userRole} userName={userName} tz={tz} />;
  return <ManagerEod venueId={venueId} venueName={venueName} tz={tz} userId={userId} userName={userName} userRole={userRole} request={request} />;
}

function ManagerEod({ venueId, venueName, tz, userId, userName, userRole, request }: Props) {
  const c = useAppColors();
  const tonight = useMemo(() => tradingDate(tz), [tz]);
  const [date, setDate] = useState(tonight);
  const [view, setView] = useState<EodView>(request?.view ?? 'hub');
  const [step, setStep] = useState<EodStepId | null>(null);
  const night = useEodNight(venueId, date, tz, true);

  useEffect(() => { if (request) { setView(request.view); setStep(null); } }, [request?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (v: EodView, s?: EodStepId) => { setStep(s ?? null); setView(v); };
  const back = () => { setView('hub'); void night.reload(); };

  if (view === 'checkin') return <EodPage venueId={venueId} userId={userId} userRole={userRole} userName={userName} tz={tz} onBack={back} />;
  if (view === 'team') {
    return <EodManagerPage venueId={venueId} date={date} maxDate={tonight} onDate={setDate} tz={tz}
      checkins={night.checkins} people={night.people} loading={night.loading} remind={night.remind} onBack={back} />;
  }
  if (view === 'numbers') {
    return (
      <Suspense fallback={<div className="flex justify-center py-24"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>}>
        <EodNumbersPage venueId={venueId} userId={userId} userName={userName} venueName={venueName}
          date={date} maxDate={tonight} onDate={setDate} initialStep={step} onBack={back} onSubmitted={() => void night.reload()} />
      </Suspense>
    );
  }
  return <EodHub userId={userId} date={date} maxDate={tonight} onDate={setDate} tz={tz}
    checkins={night.checkins} people={night.people} numbers={night.numbers} loading={night.loading} onOpen={open} />;
}
